import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { Subscription, from } from 'rxjs';
import { concatMap, toArray } from 'rxjs/operators';
import { SseService } from 'src/app/services/SseService';
import { VentanaMinimizable } from 'src/app/utils/ventana-minimizable.util';
import { TemaService } from 'src/app/services/tema.services';
import { ToastrService } from 'ngx-toastr';

interface ListaResponse {
    idlista: number;
    nombre: string;
    userid: number;
    listaIndicadores: string[] | null;
}

@Component({
    selector: 'app-dialog-guardar',
    templateUrl: './dialog-guardar.component.html',
    styleUrls: ['./dialog-guardar.component.scss']
})
export class DialogGuardarComponent implements OnInit {
    destinos = [
        { id: 'favoritos', nombre: 'Ver más tarde', seleccionado: false, bloqueado: false },
    ];
    public isDark: boolean = false;
    public subtema: Subscription;
    nuevaLista: string = '';
    mostrarNuevaLista: boolean = false;
    isLoading: boolean = false;
    isSaving: boolean = false;
    token: string = '';
    tipoLista: string = 'listas';
    constructor(
        public dialogRef: MatDialogRef<DialogGuardarComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private _cookieService: CookieService, private _sseservice: SseService, private temaservicio: TemaService,
        private toastr: ToastrService
    ) {
        this.ventana = new VentanaMinimizable(this.dialogRef, !!this.data?.flotante, '450px');
    }

    /** Minimizar/restaurar cuando se abre como ventana flotante (data.flotante). */
    ventana: VentanaMinimizable;

    ngOnInit(): void {
        this.cargarListas();
        this.subtema = this.temaservicio.valDarkTheme.subscribe({
            next: (data: any) => {
                this.isDark = data;

            }
        });
    }

    cargarListas(): void {
        this.isLoading = true;

        // Obtener token del almacenamiento si es necesario
        this.token = this._cookieService.get('auth_token');
        if (this.tipoLista === 'listas')
            this._sseservice.apiPostNew('conditions/listasByUserId', {}, this.token)
                .subscribe({
                    next: (response: any) => {
                        if (response && Array.isArray(response)) {
                            this.destinos = response.map((item: ListaResponse) => ({
                                id: item.idlista.toString(),
                                nombre: item.nombre,
                                seleccionado: false,
                                bloqueado: false
                            }));
                        } else {
                            console.error('Respuesta no esperada:', response);
                        }
                        this.isLoading = false;
                    },
                    error: (error: any) => {
                        console.error('Error al cargar listas:', error);
                        // Mantener los valores predeterminados en caso de error
                        this.isLoading = false;
                    }
                });
        else if (this.tipoLista === 'tecnicos')
            this._sseservice.apiPostNew('conditions/listasTecnicos', {}, this.token)
                .subscribe({
                    next: (response: any) => {
                        if (response && Array.isArray(response)) {
                            this.destinos = response.map((item: ListaResponse) => ({
                                id: item.idlista.toString(),
                                nombre: item.nombre,
                                seleccionado: false,
                                bloqueado: false
                            }));
                        } else {
                            console.error('Respuesta no esperada:', response);
                        }
                        this.isLoading = false;
                    },
                    error: (error: any) => {
                        console.error('Error al cargar listas:', error);
                        // Mantener los valores predeterminados en caso de error
                        this.isLoading = false;
                    }
                });
        else if (this.tipoLista === 'ratios')
            this._sseservice.apiPostNew('conditions/listasRatios', {}, this.token)
                .subscribe({
                    next: (response: any) => {
                        if (response && Array.isArray(response)) {
                            this.destinos = response.map((item: ListaResponse) => ({
                                id: item.idlista.toString(),
                                nombre: item.nombre,
                                seleccionado: false,
                                bloqueado: false
                            }));
                        } else {
                            console.error('Respuesta no esperada:', response);
                        }
                        this.isLoading = false;
                    },
                    error: (error: any) => {
                        console.error('Error al cargar listas:', error);
                        // Mantener los valores predeterminados en caso de error
                        this.isLoading = false;
                    }
                });
    }

    cerrarDialogo(): void {
        this.dialogRef.close();
    }

    guardar(): void {
        const destinosSeleccionados = this.destinos
            .filter(d => d.seleccionado)
            .map(d => d.id);

        if (destinosSeleccionados.length === 0) {
            this.toastr.error('Selecciona al menos una lista de destino');
            return;
        }

        // data.condiciones tiene prioridad; data.condicion se mantiene por compatibilidad
        // con quien todavía llama al diálogo con una sola condición (p.ej. Planificador).
        const condiciones: string[] = (this.data.condiciones && this.data.condiciones.length > 0)
            ? this.data.condiciones
            : [this.data.condicion];

        this.isSaving = true;
        this.token = this._cookieService.get('auth_token');

        // Se guarda cada condición completa tal cual (sin partirla por AND: una idea son
        // condiciones alternativas, no una descomposición de una condición compuesta),
        // y luego se enlaza a cada destino seleccionado. Se encadenan con concatMap para
        // no disparar N*M peticiones en paralelo ni cerrar el diálogo más de una vez.
        from(condiciones).pipe(
            concatMap((condicion: string) => {
                const condiccion = { indicador: condicion.replaceAll('#', ''), jsonData: '' };
                return this._sseservice.apiPostNew('conditions/condiciones', condiccion, this.token).pipe(
                    concatMap((response: any) =>
                        from(destinosSeleccionados).pipe(
                            concatMap((destino: string) =>
                                this._sseservice.apiPostNew('conditions/lista-condiciones', { idlista: destino, idindicador: response.idindicador }, this.token)
                            ),
                            toArray()
                        )
                    )
                );
            }),
            toArray()
        ).subscribe({
            next: (resultados: any[][]) => {
                this.isSaving = false;
                // El backend marca con yaEnLista la condicion que ya estaba en esa lista
                const relaciones = resultados.flat();
                const yaEstaban = relaciones.filter(r => r?.yaEnLista).length;
                const todoYaEstaba = relaciones.length > 0 && yaEstaban === relaciones.length;
                if (todoYaEstaba) {
                    this.toastr.warning(relaciones.length === 1
                        ? 'La condición ya estaba registrada en esa lista'
                        : 'Las condiciones ya estaban registradas en las listas seleccionadas');
                } else if (yaEstaban > 0) {
                    this.toastr.warning(`${yaEstaban} de ${relaciones.length} ya estaban registradas; se ha guardado el resto`);
                }
                this.dialogRef.close({
                    destinos: destinosSeleccionados,
                    nuevaLista: this.nuevaLista,
                    todoYaEstaba
                });
            },
            error: (error: any) => {
                console.error('Error al guardar condiciones:', error);
                this.toastr.error('Error al guardar la condición');
                this.isSaving = false;
            }
        });
    }

    toggleNuevaLista(): void {
        this.mostrarNuevaLista = !this.mostrarNuevaLista;
    }

    crearNuevaLista(): void {
        if (this.nuevaLista.trim()) {
            // Podríamos hacer una llamada API para crear la lista en el servidor
            // this._sseservice.apiPostNew('conditions/crear-lista', {...}, token)
            this._sseservice.apiPostNew('conditions/listas', { nombre: this.nuevaLista }, this.token).subscribe({
                next: (response: any) => {
                    console.log('Nueva lista creada:', response);
                    this.destinos.push({
                        id: response.idlista.toString(),
                        nombre: this.nuevaLista,
                        seleccionado: true,
                        bloqueado: false
                    });
                    this.nuevaLista = '';
                    this.mostrarNuevaLista = false;
                },
                error: (error: any) => {
                    console.error('Error al crear nueva lista:', error);
                }
            });
        }
    }
    restoreWithOriginalWords(sentence: string, wordsToReplace: string[]): string {
        let words = sentence.split('"');
        words = words.map(word => {
            if (word.startsWith('indicador')) {
                const index = parseInt(word.replace('indicador_', '')) - 1;
                return wordsToReplace[index].replaceAll('_', ' ');
            }
            return word;
        });

        return words.join('"');
    }
    replaceWithIndicators(sentence: string, indicadoresIA: string[]): string {
        let words = sentence.split(' ');
        let indicatorIndex = 1;
        console.log("Estos son los indicadores: ", this.data.indicatorsIaSinEspacios.length);
        words = words.map(word => {
            const index = this.data.indicatorsIaSinEspacios.indexOf(word);
            if (index !== -1) { indicadoresIA.push(word) }
            return index !== -1 ? `indicador_${indicatorIndex++}` : word;
        });

        return words.join(' ');
    }
}